/**
 * 拖拽对齐辅助线渲染（仅拖拽中显示）
 * 线宽与选择框一致：世界坐标 strokePx + var(--accent)，不用 border 类避免叠粗。
 */
import type { CSSProperties } from 'react';
import type { AlignmentGuideLine } from '@/components/canvas/transform/alignmentGuides';
import { selectionChromeWorldMetrics } from '@/components/canvas/selection/selectionChrome';

const GUIDE_COLOR = 'var(--accent)';

interface AlignmentGuideLayerProps {
  guides: AlignmentGuideLine[];
  zoom: number;
}

/** 「×」臂长（屏幕恒定，与线宽同比例） */
function guideMarkerArmWorld(zoom: number, strokePx: number): number {
  return strokePx * 4;
}

/** 对角「×」：两条等宽矩形绕中心旋转 ±45° */
function GuideCross({
  cx,
  cy,
  strokePx,
  arm,
}: {
  cx: number;
  cy: number;
  strokePx: number;
  arm: number;
}) {
  const half = arm / 2;
  const barStyle: CSSProperties = {
    position: 'absolute',
    left: cx - strokePx / 2,
    top: cy - half,
    width: strokePx,
    height: arm,
    backgroundColor: GUIDE_COLOR,
    transformOrigin: '50% 50%',
    pointerEvents: 'none',
  };
  return (
    <>
      <div style={{ ...barStyle, transform: 'rotate(45deg)' }} aria-hidden />
      <div style={{ ...barStyle, transform: 'rotate(-45deg)' }} aria-hidden />
    </>
  );
}

function VerticalGuide({
  x,
  y0,
  y1,
  strokePx,
  arm,
}: {
  x: number;
  y0: number;
  y1: number;
  strokePx: number;
  arm: number;
}) {
  const top = Math.min(y0, y1);
  const height = Math.abs(y1 - y0);
  const mid = (y0 + y1) / 2;
  return (
    <>
      <div
        className="absolute pointer-events-none"
        style={{
          left: x - strokePx / 2,
          top,
          width: strokePx,
          height,
          backgroundColor: GUIDE_COLOR,
        }}
        aria-hidden
      />
      <GuideCross cx={x} cy={y0} strokePx={strokePx} arm={arm} />
      <GuideCross cx={x} cy={y1} strokePx={strokePx} arm={arm} />
      <GuideCross cx={x} cy={mid} strokePx={strokePx} arm={arm} />
    </>
  );
}

function HorizontalGuide({
  y,
  x0,
  x1,
  strokePx,
  arm,
}: {
  y: number;
  x0: number;
  x1: number;
  strokePx: number;
  arm: number;
}) {
  const left = Math.min(x0, x1);
  const width = Math.abs(x1 - x0);
  const mid = (x0 + x1) / 2;
  return (
    <>
      <div
        className="absolute pointer-events-none"
        style={{
          left,
          top: y - strokePx / 2,
          width,
          height: strokePx,
          backgroundColor: GUIDE_COLOR,
        }}
        aria-hidden
      />
      <GuideCross cx={x0} cy={y} strokePx={strokePx} arm={arm} />
      <GuideCross cx={x1} cy={y} strokePx={strokePx} arm={arm} />
      <GuideCross cx={mid} cy={y} strokePx={strokePx} arm={arm} />
    </>
  );
}

export function AlignmentGuideLayer({ guides, zoom }: AlignmentGuideLayerProps) {
  if (guides.length === 0) return null;

  const { strokePx } = selectionChromeWorldMetrics(zoom);
  const arm = guideMarkerArmWorld(zoom, strokePx);

  return (
    <div className="pointer-events-none absolute inset-0 z-[6]" aria-hidden>
      {guides.map((g, i) => {
        const key = `${g.kind}-${g.orientation}-${g.position}-${g.start}-${i}`;
        if (g.orientation === 'vertical') {
          return (
            <VerticalGuide
              key={key}
              x={g.position}
              y0={g.start}
              y1={g.end}
              strokePx={strokePx}
              arm={arm}
            />
          );
        }
        return (
          <HorizontalGuide
            key={key}
            y={g.position}
            x0={g.start}
            x1={g.end}
            strokePx={strokePx}
            arm={arm}
          />
        );
      })}
    </div>
  );
}
